import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useGlobalContext } from '../state/GlobalContext';
import { fetchMoments, claimRewardFetch } from './moments.api';
import {
  ANIMATION_CLASSNAME,
  MomentApplication,
  MomentApplicationMutate,
} from './moments.types';
import { useTranslation } from 'react-i18next';

export const useMomentsDataFetching = (
  subscriptionId: string,
  showErrorToast: (message: string) => void,
  animate: (className: string) => void,
): {
  moments: MomentApplication[];
  mutate: MomentApplicationMutate;
  isLoading: boolean;
  isClaiming: boolean;
} => {
  const { t } = useTranslation();
  const { authToken, shopHasActiveMoments } = useGlobalContext();

  const queryClient = useQueryClient();

  const queryKey = ['moments', subscriptionId];

  const { data: moments = [], isLoading } = useQuery<
    MomentApplication[],
    Error
  >(queryKey, () => fetchMoments(subscriptionId, authToken), {
    enabled: shopHasActiveMoments,
  });

  const { mutate, isLoading: isClaiming } = useMutation(
    queryKey,
    ({
      momentId,
      productVariantGId,
    }: {
      momentId: MomentApplication['id'];
      productVariantGId?: string;
    }) =>
      claimRewardFetch({
        momentId: momentId,
        subscriptionId,
        authToken,
        productVariantGId,
      }),
    {
      onSuccess: async (momentId: MomentApplication['id']) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(queryKey);
        queryClient.setQueryData(queryKey, (old: MomentApplication[]) => {
          return old.map((m) => {
            if (m.id === momentId) {
              return { ...m, isClaimed: true };
            }
            return m;
          });
        });

        animate(`${ANIMATION_CLASSNAME}-${momentId}`);

        return [...moments];
      },
      // If the mutation fails, use the context we returned above
      onError: (err, { momentId }, context) => {
        console.error('onError', err, momentId, context);
        showErrorToast(t('subscription.failed_claimed_moment_message'));
        queryClient.setQueryData(queryKey, (old: MomentApplication[]) => [
          ...old,
        ]);
      },
    },
  );

  return { moments, mutate, isLoading, isClaiming };
};
